import React, { useEffect, useState } from "react";
import {
  Modal,
  Spin,
  Alert,
  Image,
  Button,
  Tooltip,
  Empty,
  Input,
  Pagination,
  message,
} from "antd";
import {
  FolderOpenOutlined,
  FileImageOutlined,
  FileTextOutlined,
  SearchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import {
  fetchBenchmarkFolderFiles,
  fetchBenchmarkFolderFile,
} from "@/lib/api/resources/benchmark";
import { EmlViewerModal } from "./EmlViewer";
import { IMAGE_EXTENSIONS } from "./ReferenceViewer";

const PAGE_SIZE = 24;

interface FolderContentsViewerModalProps {
  type: string;
  folderName: string;
  visible: boolean;
  onClose: () => void;
}

const AuthenticatedFolderImage: React.FC<{
  type: string;
  folderName: string;
  file: string;
}> = ({ type, folderName, file }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let isMounted = true;
    // Keep the created URL in an effect-local variable so the cleanup revokes the
    // real URL instead of a stale state closure.
    let createdUrl: string | null = null;
    setBlobUrl(null);
    setFailed(false);
    fetchBenchmarkFolderFile(type, folderName, file)
      .then((blob) => {
        if (isMounted) {
          createdUrl = URL.createObjectURL(blob);
          setBlobUrl(createdUrl);
        }
      })
      .catch(() => {
        if (isMounted) setFailed(true);
      });

    return () => {
      isMounted = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [type, folderName, file]);

  if (failed) {
    return (
      <div className="h-24 flex flex-col items-center justify-center mb-2 bg-red-50 rounded text-red-400">
        <WarningOutlined className="text-xl" />
        <span className="text-xs mt-1">Failed to load</span>
      </div>
    );
  }

  if (!blobUrl) {
    return (
      <div className="h-24 flex items-center justify-center mb-2 overflow-hidden bg-gray-100 rounded">
        <Spin size="small" />
      </div>
    );
  }

  return (
    <div className="h-24 flex items-center justify-center mb-2 overflow-hidden bg-gray-100 rounded">
      <Image
        src={blobUrl}
        alt={file}
        className="max-h-24 max-w-full object-contain"
        fallback="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='%23e5e7eb' viewBox='0 0 24 24'><path d='M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z'/></svg>"
      />
    </div>
  );
};

export const FolderContentsViewerModal: React.FC<
  FolderContentsViewerModalProps
> = ({ type, folderName, visible, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  const [selectedEmlFile, setSelectedEmlFile] = useState<string | null>(null);
  const [selectedEmlBlob, setSelectedEmlBlob] = useState<Blob | null>(null);
  const [loadingEmlFile, setLoadingEmlFile] = useState<string | null>(null);

  const {
    data: files = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["benchmark-folder-files", type, folderName],
    queryFn: () => fetchBenchmarkFolderFiles(type, folderName),
    enabled: visible && !!type && !!folderName,
  });

  useEffect(() => {
    setPage(1);
  }, [searchTerm, type, folderName]);

  useEffect(() => {
    if (!visible) {
      setSearchTerm("");
      setPage(1);
    }
  }, [visible]);

  const errorDetail = error
    ? ((error as AxiosError<{ detail?: string }>).response?.data?.detail ??
      "Failed to load files.")
    : null;

  const filteredFiles = searchTerm
    ? files.filter((f) => f.toLowerCase().includes(searchTerm.toLowerCase()))
    : files;
  const pageFiles = filteredFiles.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const openEmlPreview = async (file: string) => {
    if (loadingEmlFile) return;
    setLoadingEmlFile(file);
    try {
      const blob = await fetchBenchmarkFolderFile(type, folderName, file);
      setSelectedEmlBlob(blob);
      setSelectedEmlFile(file);
    } catch {
      message.error("Failed to load the email preview.");
    } finally {
      setLoadingEmlFile(null);
    }
  };

  return (
    <>
      <Modal
        title={
          <div className="flex items-center gap-2">
            <FolderOpenOutlined className="text-blue-500" />
            <span>Folder Contents: {folderName}</span>
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
        styles={{
          body: {
            padding: "16px",
            minHeight: "400px",
            maxHeight: "70vh",
            overflow: "auto",
          },
        }}
        destroyOnHidden
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Spin size="large" />
          </div>
        ) : errorDetail ? (
          <Alert
            type="error"
            message="Error loading folder contents"
            description={errorDetail}
          />
        ) : files.length === 0 ? (
          <Empty
            description="No files found in this folder."
            className="mt-12"
          />
        ) : (
          <>
            <div className="flex items-center justify-between gap-4 mb-4">
              <Input
                allowClear
                prefix={<SearchOutlined className="text-gray-400" />}
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-xs"
              />
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {filteredFiles.length === files.length
                  ? `${files.length} file${files.length === 1 ? "" : "s"}`
                  : `${filteredFiles.length} of ${files.length} files`}
              </span>
            </div>

            {filteredFiles.length === 0 ? (
              <Empty
                description={`No files match "${searchTerm}".`}
                className="mt-12"
              />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {pageFiles.map((file) => {
                  const ext = file.split(".").pop()?.toLowerCase() || "";
                  const isImage = IMAGE_EXTENSIONS.includes(ext);
                  const isEml = ext === "eml";

                  return (
                    <div
                      key={file}
                      className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white hover:border-blue-300 transition-colors group relative"
                    >
                      <Tooltip title={file} placement="bottom">
                        <div className="w-full text-center">
                          {isImage ? (
                            <AuthenticatedFolderImage
                              type={type}
                              folderName={folderName}
                              file={file}
                            />
                          ) : isEml ? (
                            <div
                              role="button"
                              tabIndex={0}
                              className="h-24 flex flex-col items-center justify-center mb-2 bg-gray-100 rounded cursor-pointer group-hover:bg-blue-50 transition-colors"
                              onClick={() => void openEmlPreview(file)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  void openEmlPreview(file);
                                }
                              }}
                            >
                              {loadingEmlFile === file ? (
                                <Spin size="small" />
                              ) : (
                                <FileTextOutlined className="text-4xl text-gray-400 group-hover:text-blue-500 transition-colors" />
                              )}
                              <Button
                                size="small"
                                type="link"
                                tabIndex={-1}
                                className="mt-2 text-xs opacity-0 group-hover:opacity-100"
                              >
                                Preview Mail
                              </Button>
                            </div>
                          ) : (
                            <div className="h-24 flex items-center justify-center mb-2 bg-gray-100 rounded">
                              <FileImageOutlined className="text-4xl text-gray-300" />
                            </div>
                          )}
                          <div
                            className="text-xs text-gray-600 truncate w-full px-1"
                            title={file}
                          >
                            {file}
                          </div>
                        </div>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            )}

            {filteredFiles.length > PAGE_SIZE && (
              <div className="flex justify-center mt-4">
                <Pagination
                  current={page}
                  pageSize={PAGE_SIZE}
                  total={filteredFiles.length}
                  onChange={setPage}
                  showSizeChanger={false}
                  size="small"
                />
              </div>
            )}
          </>
        )}
      </Modal>

      {selectedEmlFile && selectedEmlBlob && (
        <EmlViewerModal
          blob={selectedEmlBlob}
          filename={selectedEmlFile}
          visible={!!selectedEmlFile}
          onClose={() => {
            setSelectedEmlFile(null);
            setSelectedEmlBlob(null);
          }}
        />
      )}
    </>
  );
};
